import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { ROUTE_PATHS } from '@/lib/index';
import { IMAGES } from '@/assets/images';

interface PublicLayoutProps {
  children: React.ReactNode;
}

export function PublicLayout({ children }: PublicLayoutProps) {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const headerRef = useRef<HTMLElement>(null);
  const [headerHeight, setHeaderHeight] = useState(0);

  useEffect(() => {
    const updateHeight = () => {
      if (headerRef.current) {
        const height = headerRef.current.offsetHeight;
        setHeaderHeight(height);
      }
    };

    updateHeight();
    const resizeObserver = new ResizeObserver(updateHeight);
    if (headerRef.current) {
      resizeObserver.observe(headerRef.current);
    }

    return () => resizeObserver.disconnect();
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="min-h-screen flex flex-col">
      <header
        ref={headerRef}
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled
            ? 'bg-sidebar/95 backdrop-blur-sm shadow-lg'
            : 'bg-sidebar'
        }`}
      >
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between h-16">
            <Link to={ROUTE_PATHS.HOME} className="flex items-center gap-3">
              <img
                src={IMAGES.LOGO_EGIM_25}
                alt="EGIM Logo"
                className="h-10 w-auto object-contain"
              />
              <span className="font-bold text-xl text-primary">EGIM SM</span>
            </Link>

            <nav className="hidden md:flex items-center space-x-8">
              <Link
                to={ROUTE_PATHS.HOME}
                className="text-sm font-medium text-sidebar-foreground hover:text-primary transition-colors"
              >
                Home
              </Link>
            </nav>

            <div className="hidden md:block">
              <Button asChild variant="default">
                <Link to={ROUTE_PATHS.LOGIN}>Accedi</Link>
              </Button>
            </div>

            <button
              className="md:hidden p-2 text-sidebar-foreground"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              aria-label="Toggle menu"
            >
              {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>

        <AnimatePresence>
          {isMobileMenuOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.2 }}
              className="md:hidden bg-sidebar-accent border-t border-sidebar-border"
            >
              <div className="container mx-auto px-4 py-4 space-y-4">
                <Link
                  to={ROUTE_PATHS.HOME}
                  className="block text-sm font-medium text-sidebar-foreground hover:text-primary transition-colors py-2"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  Home
                </Link>
                <div className="pt-4 border-t border-sidebar-border">
                  <Button asChild className="w-full">
                    <Link to={ROUTE_PATHS.LOGIN}>Accedi</Link>
                  </Button>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </header>

      <main style={{ paddingTop: `${headerHeight}px` }} className="flex-1">
        {children}
      </main>

      <footer className="bg-sidebar text-sidebar-foreground py-12">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="col-span-1 md:col-span-2">
              <div className="flex items-center gap-3 mb-4">
                <img
                  src={IMAGES.LOGO_EGIM_25}
                  alt="EGIM Logo"
                  className="h-10 w-auto object-contain"
                />
                <span className="font-bold text-xl text-primary">
                  EGIM SM
                </span>
              </div>
              <p className="text-sm text-muted-foreground mb-2">
                Gestione condominiale professionale
              </p>
              <p className="text-xs text-muted-foreground">
                © 2026 EGIM SM. Tutti i diritti riservati.
              </p>
            </div>

            <div>
              <h3 className="font-semibold text-sidebar-foreground mb-4">Contatti</h3>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li>
                  <a href="mailto:info@egim.it" className="hover:text-primary transition-colors">
                    info@egim.it
                  </a>
                </li>
                <li>
                  <a href="#" className="hover:text-primary transition-colors">
                    Privacy Policy
                  </a>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}